import {
  Activity,
  FunctionComponent,
  memo,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import styles from '../custom-select.module.css';

import { TReactSelectOption } from '../../react-select/custom-react-select.types';
import Text from '@ui/text/text/text';
import useTrackBoundingClient from '@/utils/use-track-bounding-client';
import Chip from '@styles/design-system/components/chips/chip';
import { isObject } from 'underscore';
import { resizedImage } from '@/utils/resized-image';

type Props = {
  selectedOptions: TReactSelectOption[];
  selectRef: RefObject<HTMLElement | null>;
  lockActiveState: boolean;
  RenderUnfocusedLabelContent?: FunctionComponent<{
    option: TReactSelectOption;
    idx: number;
  }>;
  design?: 'flat' | 'standard';
  displayingDropdownIcon?: boolean;
  gap?: number;
  isMultiSelect: boolean;
  metaImageKey?: string | null;
  direction?: 'horizontal' | 'vertical';
  maxRows?: number;
  leftOffset?: number;
};

const CSUI_MORE_WIDTH = 50;
const CSUI_MORE_WIDTH_H_PADDING = 8;

const renderChip = ({
  o,
  isMultiSelect,
  metaImageKey,
  RenderUnfocusedLabelContent,
  idx,
}: {
  o: TReactSelectOption;
  isMultiSelect: boolean;
  metaImageKey?: string | null;
  RenderUnfocusedLabelContent?: FunctionComponent<{ option: TReactSelectOption; idx: number }>;
  idx: number;
}) => {
  const img =
    metaImageKey && isObject(o?.meta) && typeof o.meta?.[metaImageKey] === 'string' ? o.meta[metaImageKey] : undefined;
  if (RenderUnfocusedLabelContent) {
    return <RenderUnfocusedLabelContent option={o} idx={idx} />;
  }
  if (isMultiSelect) {
    return (
      <div className="flex ai-center height-100-percent">
        <Chip
          type="info"
          size={32}
          avatar={resizedImage(img, { height: 64, width: 64 })}
          label={o.label}
          noHover
          textProps={{ whiteSpace: 'normal', clamp: 1, wordBreak: 'break-all' }}
        />
      </div>
    );
  }
  return o.label;
};

/**
 * Vertical unfocused: chips wrap onto multiple rows up to maxRows. When more
 * exist, hide overflow and place a "+N" chip inline on the last visible row.
 */
const VerticalUnfocused = memo(
  ({
    selectedOptions,
    selectRef,
    lockActiveState,
    RenderUnfocusedLabelContent,
    isMultiSelect,
    metaImageKey,
    maxRows = 4,
    gap = 4,
  }: Pick<
    Props,
    | 'selectedOptions'
    | 'selectRef'
    | 'lockActiveState'
    | 'RenderUnfocusedLabelContent'
    | 'isMultiSelect'
    | 'metaImageKey'
    | 'maxRows'
    | 'gap'
  >) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const chipRefs = useRef<Array<HTMLDivElement | null>>([]);
    const [overflowFromIdx, setOverflowFromIdx] = useState<number | null>(null);

    const containerRect = useTrackBoundingClient({
      ref: selectRef,
      trackOnWindowResize: true,
      open: !!selectedOptions.length,
      rerunCounter: lockActiveState ? 1 : 0,
    });

    const recompute = useCallback(() => {
      const chips = chipRefs.current;
      const container = containerRef.current;
      const applyHides = (fromIdx: number | null) => {
        chips.forEach((c, i) => {
          if (!c) return;
          c.style.display = fromIdx !== null && i >= fromIdx ? 'none' : '';
        });
      };
      if (!Number.isFinite(maxRows) || !chips.length || !container) {
        applyHides(null);
        setOverflowFromIdx(null);
        return;
      }
      // Re-show every chip first so we measure natural layout.
      applyHides(null);
      // Build set of distinct row tops
      const rowTops: number[] = [];
      for (const c of chips) {
        if (!c) continue;
        const t = c.offsetTop;
        if (!rowTops.includes(t)) rowTops.push(t);
      }
      if (rowTops.length <= maxRows) {
        setOverflowFromIdx(null);
        return;
      }
      const lastVisibleRowTop = rowTops[maxRows - 1];
      // first chip on a row beyond maxRows
      let firstHiddenIdx = chips.findIndex((c) => c && c.offsetTop > lastVisibleRowTop);
      if (firstHiddenIdx <= 0) {
        setOverflowFromIdx(null);
        return;
      }
      // Reserve space for the "+N" chip on the last visible row. If the last visible
      // chip is wide enough that +N would wrap to a new row, hide additional chips
      // until +N fits beside the last remaining chip.
      const containerWidth = container.clientWidth;
      const moreChipReservedWidth = 60; // covers "+ 99" comfortably
      const horizontalGap = gap;
      let safety = chips.length;
      while (firstHiddenIdx > 0 && safety-- > 0) {
        const lastVisibleChip = chips[firstHiddenIdx - 1];
        if (!lastVisibleChip) break;
        if (lastVisibleChip.offsetTop !== lastVisibleRowTop) break;
        const rightEdge = lastVisibleChip.offsetLeft + lastVisibleChip.offsetWidth;
        if (rightEdge + horizontalGap + moreChipReservedWidth <= containerWidth) break;
        firstHiddenIdx--;
      }
      const next = firstHiddenIdx > 0 ? firstHiddenIdx : null;
      // Apply hides inline so we don't rely on overflowFromIdx changing — adding new
      // chips often results in the same index, but newly-rendered chips at the end
      // still need to be hidden.
      applyHides(next);
      setOverflowFromIdx(next);
    }, [maxRows, gap]);

    useLayoutEffect(() => {
      recompute();
      // Re-measure once more after Activity has flipped visible — measurements
      // taken on the same frame as the display change can read stale offsetTop.
      const raf = requestAnimationFrame(recompute);
      return () => cancelAnimationFrame(raf);
    }, [recompute, selectedOptions, containerRect?.width, lockActiveState]);

    const remaining = overflowFromIdx !== null ? selectedOptions.length - overflowFromIdx : 0;
    const showMore = remaining > 0;

    return (
      <Activity mode={lockActiveState ? 'hidden' : 'visible'}>
        <div
          ref={containerRef}
          data-component="CustomSelectUnfocusedItems"
          className={styles['unfocused-vertical-container']}
          style={{ columnGap: `${gap}px` }}
          onPointerDown={(e) => e.preventDefault()}
        >
          {selectedOptions.map((o, idx) => (
            <div
              key={`${o.value}`}
              ref={(el) => {
                chipRefs.current[idx] = el;
              }}
              className={styles['unfocused-vertical-chip']}
            >
              {renderChip({ o, isMultiSelect, metaImageKey, RenderUnfocusedLabelContent, idx })}
            </div>
          ))}
          {showMore && (
            <div
              className={`flex ai-center jc-center ${styles.moreChip} ${styles['moreChip-inline']}`}
              style={{ padding: `3px ${CSUI_MORE_WIDTH_H_PADDING}px` }}
            >
              <Text color="var(--local-chip-input-default-icotext)" className="L2 text-center" whiteSpace="nowrap">
                + {remaining}
              </Text>
            </div>
          )}
        </div>
      </Activity>
    );
  }
);
VerticalUnfocused.displayName = 'VerticalUnfocused';

const CustomSelectUnfocusedItems = memo(
  ({
    displayingDropdownIcon,
    lockActiveState,
    selectRef,
    selectedOptions,
    RenderUnfocusedLabelContent,
    design,
    gap: propsGap,
    isMultiSelect,
    metaImageKey,
    direction = 'horizontal',
    maxRows = 4,
    leftOffset,
  }: Props) => {
    const gap = propsGap ?? (direction === 'vertical' ? 4 : 8);
    const refs = useRef<Array<HTMLDivElement | null>>([]);

    const containerRect = useTrackBoundingClient({
      ref: selectRef,
      trackOnWindowResize: true,
      open: !!selectedOptions.length,
      rerunCounter: lockActiveState ? 1 : 0,
    });

    const iconOffset = displayingDropdownIcon ? 30 : 0;
    const designOffset = design === 'standard' ? 6 : 0;
    const widthOffset = designOffset + iconOffset;

    const containerWidthNoOffset = containerRect?.width || 0;
    const containerWidthWithOffsets = containerWidthNoOffset - widthOffset;

    const labelHeight = useMemo(() => {
      const labelEl = selectRef?.current?.querySelector(`.${styles['label']}`);
      if (!labelEl) {
        return '100%';
      }
      return `${labelEl.getBoundingClientRect().height}px`;
    }, [selectRef]);

    const remainingWidth = useRef(containerWidthWithOffsets);
    const hasReachedOverflow = useRef<null | number>(null);
    const [overflowIdx, setOverflowIdx] = useState<null | number>(null);

    const calculateAndApplyItemWidths = useCallback(
      (idx: number) => {
        const el = refs.current[idx];
        if (!el) return;

        el.style.minWidth = 'max-content';
        if (idx === 0) {
          hasReachedOverflow.current = null;
          remainingWidth.current = containerWidthWithOffsets;
        }

        let moreWidth = CSUI_MORE_WIDTH + gap * 2;
        /* readabilityOffset means if there's at least this much space at the end, then we will show a truncated option */
        const readabilityOffset = 40;

        const elWidth = el.getBoundingClientRect().width;
        if (elWidth + gap + readabilityOffset > remainingWidth.current - moreWidth) {
          if (idx === selectedOptions.length - 1) {
            moreWidth = gap;
          }

          if (hasReachedOverflow.current === null) {
            // First overflow at a non-last item: entering "+more" mode — the dropdown icon
            // won't be shown, so reclaim its space for the last visible item
            if (idx !== selectedOptions.length - 1) {
              remainingWidth.current += iconOffset;
            }
            hasReachedOverflow.current = idx;
          } else if (idx > hasReachedOverflow.current) {
            el.style.display = 'none';
          }

          /* 30 is just a magic number that accounts for the width of the ellipses */
          el.style.maxWidth = `${remainingWidth.current - moreWidth}px`;
          el.style.minWidth = `${remainingWidth.current - moreWidth}px`;
          el.style.width = `${remainingWidth.current - moreWidth}px`;
        }
        remainingWidth.current = remainingWidth.current - elWidth - gap;

        if (idx === selectedOptions.length - 1) {
          // loops complete, sync ref to state to trigger re-render for +more chip correctly
          const val = hasReachedOverflow.current;
          setOverflowIdx((prev) => (prev === val ? prev : val));
        }
      },
      [containerWidthWithOffsets, selectedOptions.length, setOverflowIdx, gap, iconOffset]
    );

    const selectRefClientWidth = selectRef?.current?.clientWidth || 0;

    const shouldDisplayMore =
      selectedOptions.length > 1 && !(overflowIdx === null || overflowIdx === selectedOptions.length - 1);

    const calculatedItemsContainerWidthWithOffsets = useMemo(() => {
      return `${selectRefClientWidth - designOffset - (shouldDisplayMore ? CSUI_MORE_WIDTH : iconOffset)}px`;
    }, [designOffset, iconOffset, selectRefClientWidth, shouldDisplayMore]);

    // Update container class based on shouldDisplayMore to avoid :has() performance cost
    // while the has won't have much cost on small lists, it will on long ones, so we will just go with the JS
    // approach which will be more performant
    useEffect(() => {
      if (direction === 'vertical') return;
      const container = selectRef?.current?.closest(`.${styles['custom-select-container']}`);
      if (container) {
        if (shouldDisplayMore) {
          container.classList.add(styles['has-more-items']);
        } else {
          container.classList.remove(styles['has-more-items']);
        }
      }
      // Cleanup on unmount
      return () => {
        container?.classList.remove(styles['has-more-items']);
      };
    }, [shouldDisplayMore, selectRef, lockActiveState, selectedOptions.length, direction]);

    if (direction === 'vertical') {
      if (!selectedOptions.length) return null;
      return (
        <VerticalUnfocused
          selectedOptions={selectedOptions}
          selectRef={selectRef}
          lockActiveState={lockActiveState}
          RenderUnfocusedLabelContent={RenderUnfocusedLabelContent}
          isMultiSelect={isMultiSelect}
          metaImageKey={metaImageKey}
          maxRows={maxRows}
          gap={gap}
        />
      );
    }

    if (!containerWidthWithOffsets || !selectedOptions.length) return null;
    return (
      <Activity mode={lockActiveState ? 'hidden' : 'visible'}>
        <div className="flex ai-center">
          <div
            key={selectedOptions.length}
            data-component="CustomSelectUnfocusedItems"
            style={{
              width: calculatedItemsContainerWidthWithOffsets,
              minWidth: calculatedItemsContainerWidthWithOffsets,
              maxWidth: calculatedItemsContainerWidthWithOffsets,
              height: labelHeight,
              ...(designOffset
                ? {
                    paddingRight: `${designOffset}px`,
                  }
                : {}),
            }}
            onPointerDown={(e) => {
              e.preventDefault();
            }}
            className={`z-1 overflow-hidden top-0 left-${leftOffset ?? (isMultiSelect ? 8 : 16)} position-absolute flex ai-center jc-flex-start gap-${gap}`}
          >
            {selectedOptions.map((o, idx) => {
              const img =
                metaImageKey && isObject(o?.meta) && typeof o.meta?.[metaImageKey] === 'string'
                  ? o.meta[metaImageKey]
                  : undefined;
              return (
                <Text<HTMLDivElement>
                  key={`${o.value} ${containerRect?.width || 0}`}
                  clamp={RenderUnfocusedLabelContent ? undefined : 1}
                  whiteSpace="normal"
                  width="fit-content"
                  height={RenderUnfocusedLabelContent ? '100%' : undefined}
                  wordBreak="break-all"
                  minWidth="max-content"
                  className={styles.unfocusedSelectedOption}
                  maxWidth={`${containerWidthWithOffsets}px`}
                  ref={(r) => {
                    if (!r) return;

                    refs.current[idx] = r;

                    calculateAndApplyItemWidths(idx);
                  }}
                >
                  {RenderUnfocusedLabelContent ? (
                    <RenderUnfocusedLabelContent option={o} idx={idx} />
                  ) : isMultiSelect ? (
                    <div className="flex ai-center height-100-percent">
                      <Chip
                        type="info"
                        size={32}
                        avatar={resizedImage(img, { height: 64, width: 64 })}
                        label={o.label}
                        noHover
                        textProps={{ whiteSpace: 'normal', clamp: 1, wordBreak: 'break-all' }}
                      />
                    </div>
                  ) : (
                    o.label
                  )}
                </Text>
              );
            })}
          </div>
          <div
            className={`flex ai-center jc-center position-absolute right-6 ${styles.moreChip} ${shouldDisplayMore ? styles['has-more'] : ''}`}
            style={{
              width: `${CSUI_MORE_WIDTH}px`,
              padding: `3px ${CSUI_MORE_WIDTH_H_PADDING}px`,
              display: shouldDisplayMore ? 'flex' : 'none',
            }}
          >
            <Text color="var(--local-chip-input-default-icotext)" className="L2 text-center" whiteSpace="nowrap">
              + {selectedOptions.length - 1 - (overflowIdx || 0)}
            </Text>
          </div>
        </div>
      </Activity>
    );
  }
);

CustomSelectUnfocusedItems.displayName = 'CustomSelectUnfocusedItems';

export default CustomSelectUnfocusedItems;
